import React, { useEffect, useState } from 'react';
import { Link, Outlet, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { fetchRecruiterMe } from '../api/talent';
import AnimatedBackground from './components/AnimatedBackground';
import { recruiterAuth } from './recruiterAuth';
import styles from './RecruiterLayout.module.css';

export default function RecruiterLayout() {
  const navigate = useNavigate();
  const [email, setEmail] = useState(() => recruiterAuth.email);
  const [credits, setCredits] = useState(null);

  useEffect(() => {
    const syncAuth = () => setEmail(recruiterAuth.email);
    syncAuth();
    window.addEventListener('storage', syncAuth);
    return () => window.removeEventListener('storage', syncAuth);
  }, []);

  /* Let fixed gradient canvas show through for backdrop-filter glass */
  useEffect(() => {
    const { documentElement: html, body } = document;
    const prevHtml = html.style.background;
    const prevBody = body.style.background;
    html.style.background = 'transparent';
    body.style.background = 'transparent';
    return () => {
      html.style.background = prevHtml;
      body.style.background = prevBody;
    };
  }, []);

  useEffect(() => {
    if (!recruiterAuth.isLoggedIn()) {
      setCredits(null);
      return undefined;
    }

    let cancelled = false;

    const load = () => {
      fetchRecruiterMe()
        .then((data) => {
          if (!cancelled) setCredits(data.credits ?? 0);
        })
        .catch(() => {
          if (!cancelled) setCredits(null);
        });
    };

    load();
    const interval = window.setInterval(load, 60_000);
    return () => {
      cancelled = true;
      window.clearInterval(interval);
    };
  }, [email]);

  const handleLogout = () => {
    recruiterAuth.clear();
    setEmail(null);
    setCredits(null);
    navigate('/recruiter/login', { replace: true });
  };

  const creditsPillClass =
    credits === 0
      ? `${styles.creditsPill} ${styles.creditsPillEmpty}`
      : styles.creditsPill;

  return (
    <motion.div
      className={styles.shell}
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.35 }}
    >
      <AnimatedBackground />

      <header className={styles.nav}>
        <div className={styles.navLeft}>
          <Link to="/recruiter/search" className={styles.logo}>
            <span className={styles.logoMark} aria-hidden="true">
              S
            </span>
            <span className={styles.brandName}>Samastix</span>
          </Link>
          <span className={styles.navDivider} aria-hidden="true" />
          <span className={styles.navTitle}>Talent Search</span>
        </div>

        <div className={styles.navRight}>
          {email && credits !== null && (
            <span className={creditsPillClass}>
              {credits === 0 ? (
                <>
                  No credits —{' '}
                  <a href="#buy-credits" className={styles.creditsLink} onClick={(e) => e.preventDefault()}>
                    Buy credits
                  </a>
                </>
              ) : (
                <>You have {credits} credit{credits === 1 ? '' : 's'}</>
              )}
            </span>
          )}
          {email ? (
            <>
              <span className={styles.navEmail}>{email}</span>
              <button type="button" className={styles.signOutBtn} onClick={handleLogout}>
                Sign out
              </button>
            </>
          ) : (
            <>
              <Link className={styles.navLink} to="/recruiter/login">
                Sign in
              </Link>
              <Link className={styles.navLink} to="/recruiter/signup">
                Create account
              </Link>
            </>
          )}
        </div>
      </header>

      <main className={styles.main}>
        <Outlet />
      </main>
    </motion.div>
  );
}
